// src/components/recipient/RecipientFlow.tsx
import { useState, useEffect, useCallback, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { RecipientProposal } from "./RecipientProposal";
import { RecipientLetter } from "./RecipientLetter";
import { RecipientBouquet } from "./RecipientBouquet";
import { RecipientLoading } from "./RecipientLoading";
import { RecipientCompletion } from "./RecipientCompletion";
import { RecipientMusic } from "./RecipientMusic";
import { CardData } from "../../types/card.types";
import { ARCHETYPES, ArchetypeId } from "../../constants/archetypes";
import { cardService } from "../../services/card.service";
import { FlowStep, EmotionalTheme } from "../../types/recipient.types";
import { useResponsive } from "../../hooks/useResponsive";
import { useCampaign } from "../../campaigns/providers/CampaignProvider";
import { useDecryptedCardContent } from "../../hooks/useDecryptedCardContent";

interface RecipientFlowProps {
  card: CardData;
  onComplete?: () => void;
  isPreview?: boolean;
  nextIsPay?: boolean;
  shouldAutoAdvance?: boolean;
}

const FLOW_STEPS: FlowStep[] = ["loading", "proposal", "letter", "bouquet", "completed"];

const TITLE_TO_ARCHETYPE: Record<string, ArchetypeId> = {
  "You Did It!": "moments",
  "To My Healing Heart": "safehaven",
  "To My Love": "heartbeat",
};

const getArchetypeFromCard = (card: CardData): ArchetypeId => {
  if (card.archetype && card.archetype in ARCHETYPES) {
    return card.archetype as ArchetypeId;
  }

  if (card.title && TITLE_TO_ARCHETYPE[card.title]) {
    return TITLE_TO_ARCHETYPE[card.title];
  }

  return "heartbeat";
};

export function RecipientFlow({
  card,
  onComplete,
  isPreview = false,
  nextIsPay = false,
  shouldAutoAdvance = false,
}: RecipientFlowProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const [currentStep, setCurrentStep] = useState<FlowStep>("loading");

  const { isMobile } = useResponsive();
  const { activeCampaign } = useCampaign();

  /**
   * All hooks must be declared before any conditional return.
   * Do not move this below decrypted.loading/decrypted.error returns.
   */
  const decrypted = useDecryptedCardContent(card);

  const archetypeId = useMemo(() => getArchetypeFromCard(card), [card]);

  const archConfig = useMemo(() => {
    return ARCHETYPES[archetypeId] || ARCHETYPES.heartbeat;
  }, [archetypeId]);

  const decryptedCard = useMemo<CardData>(
    () => ({
      ...card,
      message: decrypted.message || card.message,
      personalNote: decrypted.personalNote || card.personalNote,
      memory: decrypted.memory || card.memory,
      insideJoke: decrypted.insideJoke || card.insideJoke,
    }),
    [card, decrypted.message, decrypted.personalNote, decrypted.memory, decrypted.insideJoke]
  );

  const theme: EmotionalTheme = useMemo(
    () => ({
      primary: decryptedCard.primaryColor || archConfig.defaultCardData.primaryColor,
      secondary: decryptedCard.secondaryColor || archConfig.defaultCardData.secondaryColor,
      accent: decryptedCard.accentColor || archConfig.defaultCardData.accentColor,
      ambient: `${decryptedCard.primaryColor || archConfig.defaultCardData.primaryColor}33`,
    }),
    [decryptedCard, archConfig]
  );

  const loadingConfig = useMemo(
    () => ({
      style: archConfig.loadingScreen.style,
      message:
        activeCampaign?.copy.recipientLoadingMessages?.[0] ||
        archConfig.loadingScreen.message.replace("{name}", decryptedCard.recipientName),
      progressMessages:
        activeCampaign?.copy.recipientLoadingMessages || archConfig.loadingScreen.progressMessages,
      icon: archConfig.loadingScreen.icon,
      backgroundColor: activeCampaign?.theme.primary || archConfig.loadingScreen.backgroundColor,
      particleCount: archConfig.loadingScreen.particleCount,
      archetype: archetypeId,
      theme: {
        primary: activeCampaign?.theme.primary || theme.primary,
        secondary: activeCampaign?.theme.secondary || theme.secondary,
        accent: activeCampaign?.theme.accent || theme.accent,
        ambient: activeCampaign?.theme.glow || theme.ambient,
      },
    }),
    [activeCampaign, archConfig, archetypeId, decryptedCard.recipientName, theme]
  );

  const isArchetypePreviewRoute = useCallback((): boolean => {
    return location.pathname.startsWith("/preview/archetype/");
  }, [location.pathname]);

  const hasBouquet = useMemo(() => {
    return decryptedCard.includeBouquet && (decryptedCard.bouquetAmount ?? 0) > 0;
  }, [decryptedCard.includeBouquet, decryptedCard.bouquetAmount]);

  useEffect(() => {
    if (
      currentStep === "proposal" &&
      !isPreview &&
      decryptedCard.status === "paid" &&
      !decryptedCard.viewedAt
    ) {
      cardService.markAsOpened(decryptedCard.id).catch(() => undefined);
    }
  }, [currentStep, isPreview, decryptedCard.id, decryptedCard.status, decryptedCard.viewedAt]);

  useEffect(() => {
    if (!isPreview || !shouldAutoAdvance) return;

    const timers: NodeJS.Timeout[] = [];
    const currentIndex = FLOW_STEPS.indexOf(currentStep);

    if (currentIndex < FLOW_STEPS.length - 1) {
      const timer = setTimeout(
        () => {
          const nextStep = FLOW_STEPS[currentIndex + 1];

          if (nextStep === "bouquet" && !hasBouquet) {
            setCurrentStep("completed");
            return;
          }

          setCurrentStep(nextStep);
        },
        currentStep === "letter" ? 6000 : 4000
      );

      timers.push(timer);
    }

    return () => timers.forEach(clearTimeout);
  }, [currentStep, isPreview, shouldAutoAdvance, hasBouquet]);

  const handleProposalAccept = useCallback(() => {
    setCurrentStep("letter");
  }, []);

  const handleLetterOpen = useCallback(() => {
    setCurrentStep(hasBouquet ? "bouquet" : "completed");
    if (!hasBouquet && onComplete) onComplete();
  }, [hasBouquet, onComplete]);

  const handleBouquetReceive = useCallback(() => {
    setCurrentStep("completed");
    if (onComplete) onComplete();
  }, [onComplete]);

  const buildCreateUrl = useCallback((params: Record<string, string | undefined>) => {
    const query = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value && value.trim()) {
        query.set(key, value.trim());
      }
    });

    return `/create?${query.toString()}`;
  }, []);

  const handleReplyWithLrnsy = useCallback(() => {
    if (isPreview) return;

    const receivedArchetype =
      decryptedCard.archetype && decryptedCard.archetype in ARCHETYPES
        ? decryptedCard.archetype
        : archetypeId;

    const createUrl = buildCreateUrl({
      archetype: receivedArchetype,
      recipientName: decryptedCard.senderName,
      replyToCardId: decryptedCard.id,
      intent: "reply",
    });

    navigate(createUrl);
  }, [
    isPreview,
    buildCreateUrl,
    archetypeId,
    decryptedCard.archetype,
    decryptedCard.senderName,
    decryptedCard.id,
    navigate,
  ]);

  const handleCreateOwn = useCallback(
    (arch?: string, cardId?: string) => {
      if (isPreview && nextIsPay && cardId) {
        navigate(`/payment/${cardId}`);
        return;
      }

      navigate(
        buildCreateUrl({
          archetype: arch || archetypeId,
          intent: "create",
        })
      );
    },
    [navigate, isPreview, nextIsPay, buildCreateUrl, archetypeId]
  );

  const handleReplay = useCallback(() => {
    setCurrentStep("loading");
  }, []);

  if (decrypted.loading) {
    return <RecipientLoading config={loadingConfig} onComplete={() => undefined} />;
  }

  if (decrypted.error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black px-5 text-white">
        <div className="max-w-sm rounded-[28px] border border-white/10 bg-white/[0.04] p-6 text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.28em] text-rose-200/70">
            Private experience locked
          </p>

          <h2 className="mt-4 text-2xl font-light tracking-[-0.05em]">
            We could not open this private message.
          </h2>

          <p className="mt-3 text-sm leading-6 text-white/45">{decrypted.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden bg-black">
      <RecipientMusic archetype={archetypeId} currentStep={currentStep} isMuted={isMobile} />

      <AnimatePresence mode="wait">
        {currentStep === "loading" && (
          <RecipientLoading
            key="loading"
            config={loadingConfig}
            onComplete={() => setCurrentStep("proposal")}
          />
        )}

        {currentStep === "proposal" && archConfig.proposal?.enabled && (
          <RecipientProposal
            key="proposal"
            config={{
              enabled: archConfig.proposal?.enabled ?? true,
              question:
                archConfig.proposal?.question.replace("{name}", decryptedCard.recipientName) ||
                `${decryptedCard.recipientName}, are you ready for something special?`,
              image: isArchetypePreviewRoute()
                ? archConfig.defaultCardData.proposalBgImage
                : (decryptedCard.recipientImage ?? undefined),
              yesButtonText: archConfig.proposal?.yesButtonText || "Open This Moment",
              noButtonTexts: archConfig.proposal?.noButtonTexts || [],
              floatingHearts: archConfig.proposal?.floatingHearts,
              confetti: archConfig.proposal?.confetti,
              primaryColor: theme.primary,
              secondaryColor: theme.secondary,
              archetype: archetypeId,
              theme,
              delayBeforeButtons: 3000,
            }}
            senderName={decryptedCard.senderName}
            recipientName={decryptedCard.recipientName}
            onAccept={handleProposalAccept}
          />
        )}

        {currentStep === "letter" && (
          <RecipientLetter
            key="letter"
            config={{
              title: decryptedCard.title || "To You",
              content: decryptedCard.message || "",
              signature: decryptedCard.senderName,
              style: archConfig.letter.style,
              hasSecretMessage: Boolean(decryptedCard.personalNote),
              secretMessage: decryptedCard.personalNote,
              paperTexture: archConfig.letter.paperTexture,
              seal: archConfig.letter.seal,
              accentColor: theme.accent,
              primaryColor: theme.primary,
              secondaryColor: theme.secondary,
              archetypeName: archConfig.name,
              bgImage: archConfig.defaultCardData.letterBgImage,
              hasBouquet,
              image: isArchetypePreviewRoute()
                ? archConfig.defaultCardData.proposalBgImage
                : (decryptedCard.recipientImage ?? undefined),
              theme,
            }}
            senderName={decryptedCard.senderName}
            recipientName={decryptedCard.recipientName}
            onOpen={handleLetterOpen}
            onReply={handleReplyWithLrnsy}
            isPreview={isPreview}
            campaignReplyPrompt={activeCampaign?.copy.replyPrompt}
            campaignReplyHelperText={activeCampaign?.copy.replyHelperText}
          />
        )}

        {currentStep === "bouquet" && hasBouquet && (
          <RecipientBouquet
            key="bouquet"
            config={{
              enabled: hasBouquet,
              totalValue: decryptedCard.bouquetAmount || 0,
              petals: decryptedCard.petals || 5,
              interactive: true,
              receiveButtonText: archConfig.bouquet.receiveButtonText || "Receive Your Gift",
              showValue: archConfig.bouquet.showValue || true,
              message: `A digital bouquet worth KES ${(
                decryptedCard.bouquetAmount || 0
              ).toLocaleString()}`,
              primaryColor: theme.primary,
              secondaryColor: theme.secondary,
              theme,
            }}
            senderName={decryptedCard.senderName}
            recipientName={decryptedCard.recipientName}
            onReceive={handleBouquetReceive}
          />
        )}

        {currentStep === "completed" && (
          <RecipientCompletion
            key="completed"
            config={{
              hasBouquet,
              isPreview,
              nextIsPay,
              primaryColor: theme.primary,
              secondaryColor: theme.secondary,
              recipientName: decryptedCard.recipientName,
              senderName: decryptedCard.senderName,
              cardId: decryptedCard.id,
              archetype: archetypeId,
              theme,
              campaignCopy: activeCampaign?.copy,
            }}
            onCreateOwn={() => handleCreateOwn(archetypeId, decryptedCard.id)}
            onReplay={handleReplay}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
